
import { defineComponent, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import { ElForm, ElMessage } from "element-plus";
import { api } from "@/apis/user";

/**
 * value-format: YYYY-MM-DD HH:mm:ss
 * 接口期望：年月日 时分秒（常见为年-月-日不补零；时可不补零；分秒两位）
 */
function formatNoticeApiDateTime(value: unknown): string {
  if (value instanceof Date) {
    const y = value.getFullYear();
    const m = value.getMonth() + 1;
    const d = value.getDate();
    const h = value.getHours();
    const mm = String(value.getMinutes()).padStart(2, "0");
    const ss = String(value.getSeconds()).padStart(2, "0");
    return `${y}-${m}-${d} ${h}:${mm}:${ss}`;
  }
  const s = String(value ?? "").trim();
  if (!s) return "";
  // 兼容 "YYYY-MM-DD HH:mm:ss" / "YYYY-MM-DDTHH:mm:ss"
  const normalized = s.replace("T", " ");
  const [datePart, timePart] = normalized.split(" ");
  if (!datePart || !timePart) return "";
  const dateNums = datePart.split("-").map(Number);
  const timeNums = timePart.split(":");
  if (dateNums.length !== 3 || dateNums.some(n => Number.isNaN(n))) return "";
  if (timeNums.length < 2) return "";
  const y = dateNums[0];
  const m = Number(dateNums[1]);
  const d = Number(dateNums[2]);
  const h = Number(timeNums[0]);
  const mm = String(Number(timeNums[1])).padStart(2, "0");
  const ss = String(Number(timeNums[2] ?? "0")).padStart(2, "0");
  if ([m, d, h].some(n => Number.isNaN(n))) return "";
  return `${y}-${m}-${d} ${h}:${mm}:${ss}`;
}

function toMs(value: unknown): number | null {
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  const s = String(value ?? "").trim();
  if (!s) return null;
  const normalized = s.replace("T", " ").replace(/-/g, "/");
  const ms = Date.parse(normalized);
  return Number.isNaN(ms) ? null : ms;
}

/** 仅禁止今天之前的日期；今天及未来可选 */
function disabledNoticeDate(time: Date) {
  const cell = new Date(time.getFullYear(), time.getMonth(), time.getDate());
  const today = new Date();
  const startToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  return cell.getTime() < startToday.getTime();
}

export default defineComponent({
  name: "NoticePublish",
  setup() {
    const { t } = useI18n();
    const formRef = ref<InstanceType<typeof ElForm>>();
    const submitting = ref(false);
    const form = reactive({
      date: null as [unknown, unknown] | null,
      content: ""
    });

    const rules = reactive({
      date: [
        {
          trigger: "change",
          validator: (
            _rule: unknown,
            v: unknown,
            callback: (e?: Error) => void
          ) => {
            if (!v || !Array.isArray(v) || v.length !== 2 || !v[0] || !v[1]) {
              callback(new Error(t("table.noticePublishTimeRequired")));
              return;
            }
            const startMs = toMs(v[0]);
            const endMs = toMs(v[1]);
            if (startMs == null || endMs == null) {
              callback(new Error(t("table.noticePublishTimeRequired")));
              return;
            }
            if (startMs >= endMs) {
              callback(new Error(t("table.noticePublishEndAfterStart")));
              return;
            }
            callback();
          }
        }
      ],
      content: [
        {
          required: true,
          message: t("table.noticePublishContentRequired"),
          trigger: "blur"
        }
      ]
    });

    const onSubmit = async () => {
      const f = formRef.value;
      if (!f) {
        return;
      }
      try {
        await f.validate();
      } catch {
        return;
      }
      const range = form.date;
      if (!range || range.length !== 2) {
        return;
      }
      submitting.value = true;
      console.log('提交通知- 起始： ', formatNoticeApiDateTime(range[0]))
      console.log('提交通知- 截止： ', formatNoticeApiDateTime(range[1]))
      console.log('提交通知- 内容： ', String(form.content).trim())
      try {
        const res: any = await api.detail.infoNoticeAdd({
          content: String(form.content).trim(),
          startTime: formatNoticeApiDateTime(range[0]),
          endTime: formatNoticeApiDateTime(range[1])
        });
        const body = res?.data;
        if (body?.status === 200 || body?.status === 204) {
          ElMessage.success(t("table.noticePublishSuccess"));
          form.date = null;
          form.content = "";
          f.resetFields();
        } else {
          ElMessage.error(body?.message ?? t("table.noticePublishFail"));
        }
      } catch {
        /* 全局拦截器通常已提示 */
      } finally {
        submitting.value = false;
      }
    };

    return {
      t,
      formRef,
      form,
      rules,
      submitting,
      onSubmit,
      disabledNoticeDate
    };
  }
});
